<template>
  <div class="review">
    <loading-panel :loading="loading"></loading-panel>
    <div class="row justify-content-center">
      <div class="col-lg-8 col-md-10 col-12">

        <!-- ======== Booking + game details (merged) ======== -->
        <div class="review-section gt-section">
          <div class="review-header">
            <img v-if="model.booking_info.booking.game_image_url"
                 v-twin-src="model.booking_info.booking.game_image_url"
                 alt="" class="review-header__img" />
            <div v-else class="review-header__img-placeholder">
              <i class="tim-icons icon-shape-star"></i>
            </div>
            <div class="review-header__info">
              <h4 class="review-header__title">{{ model.booking_info.booking.game_name }}</h4>
              <div class="review-header__meta">
                <span class="review-header__meta-item">
                  <i class="tim-icons icon-calendar-60"></i>
                  {{ getFormattedDate(model.booking_info.booking.booking_date) }}
                </span>
                <span class="review-header__meta-item">
                  <i class="tim-icons icon-time-alarm"></i>
                  {{ getTime(model.booking_info.booking.start_time) }}
                </span>
              </div>
            </div>
          </div>

          <div class="review-divider"></div>

          <div class="review-details">
            <div class="review-detail-row">
              <span class="review-detail-row__label">Start time</span>
              <span class="review-detail-row__value">{{ getTime(model.pre_game_notes.start_time) }}</span>
            </div>
            <div class="review-detail-row">
              <span class="review-detail-row__label">End time</span>
              <span class="review-detail-row__value">{{ getTime(model.post_game_notes.end_time) }}</span>
            </div>
            <div class="review-detail-row" v-if="hasFieldEnabled(3)">
              <span class="review-detail-row__label">Custom time</span>
              <span class="review-detail-row__value">{{ formatCustomTime(model.post_game_notes.custom_time) || '—' }}</span>
            </div>
            <div class="review-detail-row" v-if="model.display_timer_value">
              <span class="review-detail-row__label">Customer screen timer</span>
              <span class="review-detail-row__value">{{ model.display_timer_value }}</span>
            </div>
            <div class="review-detail-row" v-if="pacingSummary">
              <span class="review-detail-row__label">Pacing</span>
              <span class="review-detail-row__value">
                <span class="review-badge" :class="'review-badge--pace-' + pacingSummary.status">{{ pacingStatusLabel }}</span>
                <small v-if="pacingSummary.totalDelta !== null" class="review-pace-note">{{ formatPaceDelta(pacingSummary.totalDelta) }}, last {{ pacingSummary.sampleSize }} events</small>
              </span>
            </div>
            <div class="review-detail-row" v-if="hasFieldEnabled(6)">
              <span class="review-detail-row__label">Completed</span>
              <span class="review-detail-row__value">
                <span class="review-badge" :class="completedBadgeClass">
                  {{ isCompleted ? 'Yes' : 'No' }}
                </span>
              </span>
            </div>
            <div class="review-detail-row" v-if="hasFieldEnabled(7) && model.pre_game_notes.is_corporate_booking">
              <span class="review-detail-row__label">Corporate booking</span>
              <span class="review-detail-row__value">
                <span class="review-badge review-badge--purple">Yes</span>
              </span>
            </div>
            <div class="review-detail-row" v-if="hostFieldEnabled">
              <span class="review-detail-row__label">Host</span>
              <span class="review-detail-row__value">{{ model.host_name || 'Not set' }}</span>
            </div>
          </div>
        </div>

        <!-- ======== Team ======== -->
        <div class="review-section gt-section" v-if="confirmedUsers.length > 0 || hasFieldEnabled(8) || hasFieldEnabled(11)">
          <div class="review-section__header">
            <div class="review-section__label">
              <i class="tim-icons icon-single-02 text-white"></i>
              Group
            </div>
          </div>

          <!-- Confirmed players as chips -->
          <div v-if="confirmedUsers.length > 0" class="review-player-list">
            <div
              v-for="(user, index) in confirmedUsers"
              :key="'player-' + index"
              class="review-player"
            >
              <div class="review-player__avatar">
                {{ getInitials(user.first_name, user.last_name) }}
              </div>
              <span class="review-player__name">{{ user.first_name }}</span>
            </div>
          </div>

          <!-- Team name + experience -->
          <div v-if="hasFieldEnabled(8) || hasFieldEnabled(11)" class="review-team-meta"
               :class="{ 'review-team-meta--has-players': confirmedUsers.length > 0 }">
            <div class="review-detail-row" v-if="hasFieldEnabled(8)">
              <span class="review-detail-row__label">Team name</span>
              <span class="review-detail-row__value">{{ model.pre_game_notes.team || '—' }}</span>
            </div>
            <div class="review-detail-row" v-if="hasFieldEnabled(11)">
              <span class="review-detail-row__label">Experience</span>
              <span class="review-detail-row__value">{{ model.pre_game_notes.team_experience || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- ======== Notes and tags ======== -->
        <div class="review-section gt-section" v-if="hasFieldEnabled(9) || hasFieldEnabled(10) || bookingTags.length > 0">
          <div class="review-section__header">
            <div class="review-section__label">
              <i class="tim-icons icon-notes text-white"></i>
              Notes and tags
            </div>
          </div>
          <div class="review-details">
            <div class="review-detail-row review-detail-row--stacked" v-if="hasFieldEnabled(9)">
              <span class="review-detail-row__label">Pre-event</span>
              <span class="review-detail-row__value" v-if="model.pre_game_notes.pre_notes">{{ model.pre_game_notes.pre_notes }}</span>
              <span class="review-detail-row__value review-detail-row__value--empty" v-else>No notes entered</span>
            </div>
            <div class="review-detail-row review-detail-row--stacked" v-if="hasFieldEnabled(10)">
              <span class="review-detail-row__label">Post-event</span>
              <span class="review-detail-row__value" v-if="model.post_game_notes.post_notes">{{ model.post_game_notes.post_notes }}</span>
              <span class="review-detail-row__value review-detail-row__value--empty" v-else>No notes entered</span>
            </div>
            <div class="review-detail-row review-detail-row--stacked" v-if="bookingTags.length > 0">
              <span class="review-detail-row__label">Tags</span>
              <div class="tags-row">
                <span
                  v-for="tag in bookingTags"
                  :key="'RT' + tag.id"
                  class="tag-chip text-white"
                  :class="{ 'tag-chip--archived': tag.is_archived }"
                  :style="tagChipStyle(tag.color)"
                >
                  {{ tag.name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ======== Photo ======== -->
        <div class="review-section gt-section" v-if="hasFieldEnabled(2) && hasPhoto">
          <div class="review-section__header">
            <div class="review-section__label">
              <i class="tim-icons icon-camera-18 text-white"></i>
              Photo
            </div>
          </div>
          <div class="review-photo">
            <img :src="model.photo.overlay_url || model.photo.uploaded_image" alt="Game photo" class="review-photo__img" />
          </div>
          <div v-if="model.photo.email_to_group" class="review-photo__note">
            <i class="tim-icons icon-email-85"></i>
            Photo will be emailed to the group
          </div>
        </div>

        <!-- ======== Objectives ======== -->
        <div class="review-section gt-section" v-if="hasFieldEnabled(1) && model.puzzles.length > 0">
          <div class="review-section__header">
            <div class="review-section__label">
              <i class="tim-icons icon-bullet-list-67 text-white"></i>
              Objectives
            </div>
          </div>
          <div class="review-puzzle-list">
            <template v-for="parent in topLevelPuzzles">
              <div
                :key="'puzzle-' + parent.puzzle_id"
                class="review-puzzle gt-list-item"
                :class="{
                  'review-puzzle--completed': !parent.did_not_finish,
                  'review-puzzle--dnf': parent.did_not_finish
                }"
              >
                <div class="review-puzzle__header">
                  <span class="review-puzzle__name">{{ parent.name }}</span>
                  <span class="review-puzzle__badges">
                    <span v-if="pacingChip(parent)" class="review-badge" :class="'review-badge--pace-' + pacingChip(parent).band">{{ pacingChipLabel(pacingChip(parent)) }}</span>
                    <span class="review-badge" :class="parent.did_not_finish ? 'review-badge--red' : 'review-badge--green'">
                      {{ parent.did_not_finish ? 'Did not finish' : 'Completed' }}
                    </span>
                  </span>
                </div>
                <p v-if="parent.description" class="review-puzzle__desc">{{ parent.description }}</p>
                <div class="review-puzzle__stats">
                  <span v-if="parent.solve_time" class="review-puzzle__stat">
                    <i class="tim-icons icon-watch-time"></i> {{ getTime(parent.solve_time) }}
                  </span>
                  <span class="review-puzzle__stat">
                    <i class="tim-icons icon-bulb-63"></i> {{ parent.hints_given_count }} interaction{{ parent.hints_given_count !== 1 ? 's' : '' }}
                  </span>
                  <span v-if="parent.breakage_occurred" class="review-puzzle__stat review-puzzle__stat--warn">
                    <i class="tim-icons icon-alert-circle-exc"></i> Issue reported
                  </span>
                </div>
                <p v-if="parent.hints_given_description" class="review-puzzle__note">
                  <strong>Interactions:</strong> {{ parent.hints_given_description }}
                </p>
                <p v-if="parent.breakage_description" class="review-puzzle__note review-puzzle__note--warn">
                  <strong>Issue:</strong> {{ parent.breakage_description }}
                </p>

                <!-- Children nested inside parent -->
                <div v-if="getChildren(parent).length > 0" class="review-puzzle-children">
                  <div
                    v-for="child in getChildren(parent)"
                    :key="'puzzle-child-' + child.puzzle_id"
                    class="review-puzzle review-puzzle--child gt-list-item"
                    :class="{
                      'review-puzzle--completed': !child.did_not_finish,
                      'review-puzzle--dnf': child.did_not_finish
                    }"
                  >
                    <div class="review-puzzle__header">
                      <span class="review-puzzle__name">{{ child.name }}</span>
                      <span class="review-puzzle__badges">
                        <span v-if="pacingChip(child)" class="review-badge" :class="'review-badge--pace-' + pacingChip(child).band">{{ pacingChipLabel(pacingChip(child)) }}</span>
                        <span class="review-badge" :class="child.did_not_finish ? 'review-badge--red' : 'review-badge--green'">
                          {{ child.did_not_finish ? 'Did not finish' : 'Completed' }}
                        </span>
                      </span>
                    </div>
                    <p v-if="child.description" class="review-puzzle__desc">{{ child.description }}</p>
                    <div class="review-puzzle__stats">
                      <span v-if="child.solve_time" class="review-puzzle__stat">
                        <i class="tim-icons icon-watch-time"></i> {{ getTime(child.solve_time) }}
                      </span>
                      <span class="review-puzzle__stat">
                        <i class="tim-icons icon-bulb-63"></i> {{ child.hints_given_count }} interaction{{ child.hints_given_count !== 1 ? 's' : '' }}
                      </span>
                      <span v-if="child.breakage_occurred" class="review-puzzle__stat review-puzzle__stat--warn">
                        <i class="tim-icons icon-alert-circle-exc"></i> Issue reported
                      </span>
                    </div>
                    <p v-if="child.hints_given_description" class="review-puzzle__note">
                      <strong>Interactions:</strong> {{ child.hints_given_description }}
                    </p>
                    <p v-if="child.breakage_description" class="review-puzzle__note review-puzzle__note--warn">
                      <strong>Issue:</strong> {{ child.breakage_description }}
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  import { TimeSelect, Popover} from 'element-ui';
  import { Auth } from 'aws-amplify'
  import axios from 'axios'
  import { API_LOCATION } from '@/api-config'
  import { tagChipStyle } from '@/util/tagChip'
  import {dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
  import {formatCustomTime} from "@/util/customTime";
  import { summarizePacing, toLocalDate, formatDelta, formatDuration, statusLabel } from "@/util/trackerPacing";
    export default {
      name: "ReviewStep",
      props: {
        gameInstanceId: {
          type: [String, Number],
          default: null
        }
      },
      components: {
        [TimeSelect.name]: TimeSelect,
        [Popover.name]: Popover,
      },
      data() {
        return {
          loading: false,
          optional_fields: [],
          bookingTags: [],
          time_format: JSON.parse(localStorage.getItem('group')).time_format,
          date_format: JSON.parse(localStorage.getItem('group')).date_format,
          gts: '',
          model: {
            puzzles: [],
            booking_info: {
              booking: {
                name: ''
              }
            },
            pre_game_notes: '',
            post_game_notes: '',
            photo: '',
            pacing: null,
            pacing_end: null,
            display_timer_value: null,
            host_name: ''
          },
        }
      },
      computed: {
        hostFieldEnabled() {
          return (this.optional_fields || []).some(f => f.column_name === 'host' && f.is_enabled)
        },
        pacingSummary() {
          const ctx = this.model.pacing
          if (!ctx || !ctx.baseline || !ctx.startDate || !this.model.pacing_end) {
            return null
          }
          const endDate = toLocalDate(this.model.pacing_end)
          if (!endDate) {
            return null
          }
          return summarizePacing({
            baseline: ctx.baseline,
            puzzles: this.model.puzzles,
            startDate: ctx.startDate,
            endDate,
            limitSeconds: ctx.limitSeconds
          })
        },
        pacingStatusLabel() {
          return this.pacingSummary ? (statusLabel(this.pacingSummary.status) || 'Unknown') : ''
        },
        confirmedUsers() {
          if (!this.model.pre_game_notes || !this.model.pre_game_notes.users) return []
          return this.model.pre_game_notes.users.filter(u => u.confirmed === true)
        },
        isCompleted() {
          if (!this.model.post_game_notes) return false
          const val = this.model.post_game_notes.completed
          return val === true || val === 'true'
        },
        completedBadgeClass() {
          return this.isCompleted ? 'review-badge--green' : 'review-badge--red'
        },
        hasPhoto() {
          return this.model.photo &&
            this.model.photo.uploaded_image &&
            this.model.photo.uploaded_image !== 'https://console.offthecouch.io/img/placeholder.jpg' &&
            this.model.photo.uploaded_image !== ''
        },
        topLevelPuzzles() {
          return this.model.puzzles
            .filter(p => !p.parent_id)
            .sort((a, b) => a.position - b.position)
        }
      },
      methods: {
        tagChipStyle,
        formatCustomTime,
        draftKey(key) {
          if (!this.gameInstanceId) {
            console.warn('draftKey called with no gameInstanceId for key:', key)
            return key + '_NONE'
          }
          return key + '_' + this.gameInstanceId
        },
        formatTimeForDisplay(modelKey, field) {
          let val = this.model[modelKey] && this.model[modelKey][field]
          if (!val) return
          if (val instanceof Date) {
            this.model[modelKey][field] = ('0' + val.getHours()).slice(-2) + ':' + ('0' + val.getMinutes()).slice(-2)
          } else if (typeof val === 'string' && (val.includes('T') || val.includes(' '))) {
            this.model[modelKey][field] = this.parseTimeString(val)
          }
        },
        parseTimeString(val) {
          if (typeof val === 'string' && val.includes('T')) {
            let parts = val.split('T')[1].split(':')
            return parts[0] + ':' + parts[1]
          } else if (typeof val === 'string' && val.includes(' ')) {
            let parts = val.split(' ')[1].split(':')
            return parts[0] + ':' + parts[1]
          }
          return '00:00'
        },
        getInitials(first, last) {
          return ((first || '').charAt(0) + (last || '').charAt(0)).toUpperCase()
        },
        getChildren(parent) {
          return this.model.puzzles
            .filter(c => c.parent_id && Number(c.parent_id) === Number(parent.source_puzzle_id))
            .sort((a, b) => a.position - b.position)
        },
        setOptionalFields(fields){
          this.optional_fields = fields
        },
        hasFieldEnabled(fieldId){
          for(let n in this.optional_fields){
            if(this.optional_fields[n].field_id === fieldId && this.optional_fields[n].is_enabled){
              return true
            }
          }
          return false
        },
        getDayOfWeek(d){
          if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
            return dayOfWeek(d) + ', '
          }
          return ''
        },
        getTime(dt){
          if(!dt){
            return
          }

          if(dt.includes('T')){
            dt = dt.split('T')[1]
          } else if(dt.includes(' ')){
            dt = dt.split(' ')[1].split(':')[0] + ':' + dt.split(' ')[1].split(':')[1]
          }
          return time(this.time_format, dt)
        },
        getFormattedDate(d){
          if(d == undefined){
            return
          }
          return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
        },
        getAuthToken() {
          return new Promise(function (resolve) {
            Auth.currentSession()
              .then(data => {
                resolve(data.idToken.jwtToken)
              })
              .catch(err => console.log(err))
          })
        },
        async validate() {
          this.$emit('on-validated', true, this.model);
          return true;
        },
        pacingChip(puzzle) {
          if (!this.pacingSummary) {
            return null
          }
          const entry = this.pacingSummary.perPuzzle[puzzle.source_puzzle_id]
          if (!entry) {
            return null
          }
          if (entry.delta !== null && entry.band !== 'overdue') {
            return entry
          }
          if (entry.typical !== null && entry.typical !== undefined) {
            return { band: 'typical', delta: null, typical: entry.typical }
          }
          return null
        },
        pacingChipLabel(chip) {
          return chip.delta === null ? 'typically ' + formatDuration(chip.typical) : formatDelta(chip.delta)
        },
        formatPaceDelta(delta) {
          return formatDelta(delta)
        },
        getReviewData (gts, stepData) {
          this.gts = gts
          if (stepData) {
            // Clone objects to avoid mutating the original step component models
            this.model.booking_info = stepData.bookingInfo
            this.model.pre_game_notes = stepData.preNotes ? { ...stepData.preNotes } : this.model.pre_game_notes
            if(this.hasFieldEnabled(1)){
              this.model.puzzles = stepData.puzzles ? [...stepData.puzzles] : []
            }
            if(this.hasFieldEnabled(2)){
              this.model.photo = stepData.photo ? { ...stepData.photo } : this.model.photo
            }
            this.model.post_game_notes = stepData.postNotes ? { ...stepData.postNotes } : this.model.post_game_notes
            this.model.pacing = stepData.pacing || null
            this.model.display_timer_value = stepData.displayTimer || null
            this.model.host_name = stepData.hostName || ''
            this.model.pacing_end = stepData.postNotes && stepData.postNotes.end_time ? stepData.postNotes.end_time : null

            // Format Date objects to "HH:MM" strings for display (on the clones, not originals)
            this.formatTimeForDisplay('pre_game_notes', 'start_time')
            this.formatTimeForDisplay('post_game_notes', 'end_time')
          } else {
            this.model.booking_info = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo')))
            this.model.pre_game_notes = JSON.parse(localStorage.getItem(this.draftKey('preNotes')))
            if(this.hasFieldEnabled(1)){
              this.model.puzzles = JSON.parse(localStorage.getItem(this.draftKey('puzzles')))
            }
            if(this.hasFieldEnabled(2)){
              this.model.photo = JSON.parse(localStorage.getItem(this.draftKey('photo')))
            }
            this.model.post_game_notes = JSON.parse(localStorage.getItem(this.draftKey('postNotes')))

            // localStorage fallback: format times from draft
            let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
            if(draft){
              const startTimeValue = draft.actual_start_time || draft.start_time;
              const endTimeValue = draft.actual_end_time || draft.end_time;

              if(startTimeValue) {
                this.model.pre_game_notes.start_time = this.parseTimeString(startTimeValue)
              }
              if(endTimeValue) {
                this.model.post_game_notes.end_time = this.parseTimeString(endTimeValue)
              }
            } else {
              this.formatTimeForDisplay('pre_game_notes', 'start_time')
              this.formatTimeForDisplay('post_game_notes', 'end_time')
            }
          }
          this.loadBookingTags()
        },
        async loadBookingTags() {
          this.bookingTags = []
          const bookingInfo = this.model.booking_info
          const bookingId = bookingInfo && bookingInfo.booking && bookingInfo.booking.id
          if (!bookingId) return
          try {
            let token = await this.getAuthToken()
            let config = { headers: { Authorization: token } }
            let companyId = localStorage.getItem('companyId')
            let companyGroupId = JSON.parse(localStorage.getItem('group')).id
            let response = await axios.get(API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/bookings/' + bookingId + '/tags', config)
            this.bookingTags = response.data || []
          } catch (e) {
            console.log('Tags not available:', e.message)
          }
        },
        submitRequest() {
          localStorage.removeItem(this.draftKey('draft'))
          localStorage.removeItem(this.draftKey('bookingInfo'))
          localStorage.removeItem(this.draftKey('puzzles'))
          localStorage.removeItem(this.draftKey('postNotes'))
          localStorage.removeItem(this.draftKey('preNotes'))
          localStorage.removeItem(this.draftKey('photo'))
        }

      }
    }
</script>

<style scoped>
/* ========================================
   Layout
   ======================================== */
.review {
  padding-top: 8px;
}

/* ========================================
   Booking header (inside first section)
   ======================================== */
.review-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.review-header__img {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.review-header__img-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(225, 78, 202, 0.1);
}

.review-header__img-placeholder i {
  font-size: 20px;
  color: #e14eca;
}

.review-header__info {
  flex: 1;
  min-width: 0;
}

.review-header__title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.review-header__meta {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.review-header__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.review-header__meta-item i {
  font-size: 12px;
  color: #e14eca;
}

/* ========================================
   Divider (between header and details)
   ======================================== */
.review-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 14px 0;
}

/* ========================================
   Section containers — visual base from gt-section
   ======================================== */
.review-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.review-section__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
}

.review-section__label i {
  font-size: 14px;
}

/* ========================================
   Detail rows (key-value pairs)
   ======================================== */
.review-details {
  display: flex;
  flex-direction: column;
}

.review-detail-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.review-detail-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.review-detail-row:first-child {
  padding-top: 0;
}

.review-detail-row--stacked {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.review-detail-row__label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
}

.review-detail-row__value {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  text-align: right;
}

.review-detail-row--stacked .review-detail-row__value {
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  text-align: left;
}

.review-detail-row__value--empty {
  font-style: italic;
  color: rgba(255, 255, 255, 0.3);
}

/* ========================================
   Badges
   ======================================== */
.review-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 3px;
  letter-spacing: 0.02em;
}

.review-badge--green {
  color: #00bf9a;
  background: rgba(0, 191, 154, 0.1);
}

.review-badge--red {
  color: #fd5d93;
  background: rgba(253, 93, 147, 0.1);
}

.review-badge--purple {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.review-badge--pace-ahead,
.review-badge--pace-fast {
  color: #00bf9a;
  background: rgba(0, 191, 154, 0.18);
}

.review-badge--pace-on_pace {
  color: #1d8cf8;
  background: rgba(29, 140, 248, 0.18);
}

.review-badge--pace-behind,
.review-badge--pace-slow {
  color: #ec250d;
  background: rgba(236, 37, 13, 0.18);
}

.review-badge--pace-finished,
.review-badge--pace-typical {
  color: rgba(255, 255, 255, 0.92);
  background: rgba(125, 125, 125, 0.3);
}

.review-puzzle__badges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.review-pace-note {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

/* ========================================
   Player chips
   ======================================== */
.review-player-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.review-player {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 4px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.review-player__avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(225, 78, 202, 0.12);
  color: #e14eca;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.review-player__name {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
}

/* ========================================
   Team meta (below player chips)
   ======================================== */
.review-team-meta--has-players {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* ========================================
   Photo
   ======================================== */
.review-photo {
  text-align: center;
}

.review-photo__img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 6px;
}

.review-photo__note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
}

.review-photo__note i {
  color: #e14eca;
  font-size: 14px;
}

/* ========================================
   Puzzle cards
   ======================================== */
.review-puzzle-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.review-puzzle {
  cursor: default;
}

.review-puzzle--completed {
  border-left-color: #00bf9a;
}

.review-puzzle--dnf {
  border-left-color: #ff8d72;
}

.review-puzzle__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.review-puzzle__name {
  font-size: 14px;
  font-weight: 600;
  color: #e14eca;
}

.review-puzzle__desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 6px;
}

.review-puzzle__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 2px;
}

.review-puzzle__stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.review-puzzle__stat i {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
}

.review-puzzle__stat--warn {
  color: #ff8d72;
}

.review-puzzle__stat--warn i {
  color: #ff8d72;
}

.review-puzzle__note {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin: 4px 0 0;
}

.review-puzzle__note strong {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.review-puzzle__note--warn {
  color: #ff8d72;
}

.review-puzzle__note--warn strong {
  color: #ff8d72;
}

/* ========================================
   Nested children
   ======================================== */
.review-puzzle-children {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.review-puzzle--child {
  margin-left: 16px;
}

/* ========================================
   Responsive
   ======================================== */
@media (max-width: 576px) {
  .review-section {
    padding: 14px;
  }

  .review-header__img,
  .review-header__img-placeholder {
    width: 40px;
    height: 40px;
  }

  .review-detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .review-detail-row__value {
    text-align: left;
  }
}
</style>

<!-- Light mode overrides (unscoped) — section base handled by gt-section -->
<style>
.white-content .review-header__title {
  color: #1d253b;
}

.white-content .review-header__img-placeholder {
  background: rgba(225, 78, 202, 0.08);
}

.white-content .review-header__meta-item {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .review-divider {
  background: rgba(0, 0, 0, 0.06);
}

.white-content .review-badge--pace-typical,
.white-content .review-badge--pace-finished {
  color: rgba(0, 0, 0, 0.7);
}

.white-content .review-pace-note {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .review-section__label {
  color: #344675;
}

.white-content .review-section__label i {
  color: #344675;
}

.white-content .review-detail-row {
  border-bottom-color: rgba(0, 0, 0, 0.05);
}

.white-content .review-detail-row__label {
  color: rgba(0, 0, 0, 0.55);
}

.white-content .review-detail-row__value {
  color: #344675;
}

.white-content .review-detail-row--stacked .review-detail-row__value {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .review-detail-row__value--empty {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .review-badge--green {
  background: rgba(0, 191, 154, 0.08);
}

.white-content .review-badge--red {
  background: rgba(253, 93, 147, 0.08);
}

.white-content .review-badge--purple {
  background: rgba(225, 78, 202, 0.08);
}

.white-content .review-player {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.06);
}

.white-content .review-player__avatar {
  background: rgba(225, 78, 202, 0.1);
}

.white-content .review-player__name {
  color: #344675;
}

.white-content .review-team-meta--has-players {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.white-content .review-photo__note {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .review-puzzle--completed {
  border-left-color: #00bf9a;
}

.white-content .review-puzzle--dnf {
  border-left-color: #ff8d72;
}

.white-content .review-puzzle__name {
  color: #e14eca;
}

.white-content .review-puzzle__desc {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .review-puzzle__stat {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .review-puzzle__stat i {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .review-puzzle__note {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .review-puzzle__note strong {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .review-puzzle-children {
  border-top-color: rgba(0, 0, 0, 0.06);
}
</style>
